package com.appsyde.shared.modules.ai.data.source

import com.appsyde.shared.modules.ai.data.dto.AiErrorResponse
import com.appsyde.shared.modules.ai.data.dto.AiGenerateRequestV2
import com.appsyde.shared.modules.ai.domain.AiExtractRunningException
import com.appsyde.shared.modules.ai.data.dto.AiGenerateResponseV2
import com.appsyde.shared.modules.ai.data.dto.AiPreloadRequest
import com.appsyde.shared.modules.ai.data.dto.AiPreloadRequestV2
import com.appsyde.shared.modules.ai.data.dto.AiPreloadResponseDto
import com.appsyde.shared.modules.ai.data.dto.AiRefineRequest
import com.appsyde.shared.modules.ai.data.dto.AiRefineResponseDto
import io.ktor.client.HttpClient
import io.ktor.client.request.accept
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlinx.serialization.DeserializationStrategy
import kotlinx.serialization.json.Json

class AiRemoteDataSourceImpl(
    private val client: HttpClient,
    serverBaseUrl: String,
) : AiRemoteDataSource {

    private val json = Json { ignoreUnknownKeys = true }

    private val base = serverBaseUrl.removeSuffix("/") + "/ai"
    private val urlGenerate = "$base/generate"
    private val urlRegenerateContext = "$base/regenerate-context"
    private val urlPreload = "$base/preload"
    private val urlRefine = "$base/refine"

    override suspend fun generateExtract(request: AiGenerateRequestV2): AiGenerateResponseV2 {
        val res = client.post(urlGenerate) {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)
            setBody(request)
        }
        return parseGenerateOrThrow(res, op = "generate")
    }

    override suspend fun regenerateContext(messageId: String): AiGenerateResponseV2 {
        val res = client.post(urlRegenerateContext) {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)
            setBody(AiGenerateRequestV2(messageId = messageId, force = false))
        }
        return parseOrThrow(res, AiGenerateResponseV2.serializer(), op = "regenerate-context")
    }

    override suspend fun preload(request: AiPreloadRequest): AiPreloadResponseDto {
        val res = client.post(urlPreload) {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)
            setBody(request)
        }
        return parseOrThrow(res, AiPreloadResponseDto.serializer(), op = "preload")
    }

    override suspend fun preloadByMessageIds(request: AiPreloadRequestV2): AiPreloadResponseDto {
        val res = client.post(urlPreload) {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)
            setBody(request)
        }
        return parseOrThrow(res, AiPreloadResponseDto.serializer(), op = "preload")
    }

    override suspend fun refine(request: AiRefineRequest): AiRefineResponseDto {
        val res = client.post(urlRefine) {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)
            setBody(request)
        }
        return parseOrThrow(res, AiRefineResponseDto.serializer(), op = "refine")
    }

    private suspend fun parseGenerateOrThrow(res: HttpResponse, op: String): AiGenerateResponseV2 {
        val bodyText = res.bodyAsText()
        if (res.status.value in 200..299) {
            return json.decodeFromString(AiGenerateResponseV2.serializer(), bodyText)
        }
        if (res.status.value == 409) {
            val err = runCatching {
                json.decodeFromString(AiErrorResponse.serializer(), bodyText)
            }.getOrNull()
            throw AiExtractRunningException(
                message = err?.message ?: "AI $op: extract running (409)"
            )
        }
        throwParseError(res, bodyText, op)
    }

    private suspend fun <T> parseOrThrow(
        res: HttpResponse,
        serializer: DeserializationStrategy<T>,
        op: String,
    ): T {
        val bodyText = res.bodyAsText()
        if (res.status.value in 200..299) {
            return json.decodeFromString(serializer, bodyText)
        }
        throwParseError(res, bodyText, op)
    }

    private fun throwParseError(res: HttpResponse, bodyText: String, op: String): Nothing {
        val err = runCatching {
            json.decodeFromString(AiErrorResponse.serializer(), bodyText)
        }.getOrNull()
        if (err != null) throw RuntimeException("AI $op error: ${err.error} – ${err.message}")
        throw RuntimeException("AI $op error: HTTP ${res.status.value} – $bodyText")
    }
}
